// Sessão first-party para telemetria de UX.
// - sessionId estável em sessionStorage (persiste durante a aba).
// - Reseta após 30 min de inatividade (mesmo limiar que GA4 usa por padrão).
// - Captura contexto de dispositivo uma vez por sessão para anexar a cada evento.

const SESSION_KEY = 'ux_session_id';
const SESSION_LAST_ACT_KEY = 'ux_session_last_activity';
const SESSION_STARTED_KEY = 'ux_session_started_at';
const IDLE_MS = 30 * 60 * 1000;

let cachedDeviceContext = null;

const safeGet = (storage, key) => {
  try { return storage.getItem(key); } catch { return null; }
};

const safeSet = (storage, key, value) => {
  try { storage.setItem(key, value); } catch {}
};

const newSessionId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  const r = () => Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${r()}${r()}`;
};

const detectDeviceType = (ua) => {
  if (/iPad|Tablet|PlayBook/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua)) return 'mobile';
  return 'desktop';
};

const detectOS = (ua) => {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Other';
};

const detectBrowser = (ua) => {
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/OPR\//i.test(ua)) return 'Opera';
  if (/Chrome\//i.test(ua) && !/Edg\/|OPR\//i.test(ua)) return 'Chrome';
  if (/Firefox\//i.test(ua)) return 'Firefox';
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return 'Safari';
  return 'Other';
};

export const getDeviceContext = () => {
  if (cachedDeviceContext) return cachedDeviceContext;
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { device_type: 'unknown' };
  }
  const ua = navigator.userAgent || '';
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
  cachedDeviceContext = {
    device_type: detectDeviceType(ua),
    os: detectOS(ua),
    browser: detectBrowser(ua),
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    pixel_ratio: window.devicePixelRatio || 1,
    language: navigator.language || null,
    network: conn ? (conn.effectiveType || null) : null,
    save_data: conn ? !!conn.saveData : false,
    is_native: !!(window.AndroidBridge || window.webkit?.messageHandlers),
  };
  return cachedDeviceContext;
};

export const getSessionId = () => {
  if (typeof sessionStorage === 'undefined') return 'no-session';
  const now = Date.now();
  const last = parseInt(safeGet(sessionStorage, SESSION_LAST_ACT_KEY) || '0', 10) || 0;
  let id = safeGet(sessionStorage, SESSION_KEY);
  if (!id || (last && now - last > IDLE_MS)) {
    id = newSessionId();
    safeSet(sessionStorage, SESSION_KEY, id);
    safeSet(sessionStorage, SESSION_STARTED_KEY, String(now));
  }
  safeSet(sessionStorage, SESSION_LAST_ACT_KEY, String(now));
  return id;
};

export const getSessionStartedAt = () => {
  if (typeof sessionStorage === 'undefined') return Date.now();
  return parseInt(safeGet(sessionStorage, SESSION_STARTED_KEY) || String(Date.now()), 10);
};

export const initSession = () => {
  // Apenas garante que sessionId está pronto para o primeiro evento.
  getSessionId();
  getDeviceContext();
};
